/**
 * JobCard — Think-host plate for background job tools.
 * Built on shadcn Item. Do not reuse AI Elements Tool (that is tool-call state).
 */
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { userFacingJobError } from "@/lib/job-drain";
import {
  JOB_CANCELLED,
  JOB_COPPER,
  JOB_FALCON,
  JOB_HAIRLINE,
  JOB_INK_55,
  JOB_META,
  JOB_OK,
  JOB_RAISED,
  cancelViaThinkText,
  formatJobClock,
  formatJobEndTime,
  formatJobPillTerminalHint,
  isActiveJobState,
  jobBarColor,
  jobCardDomId,
  jobElapsedMs,
  jobPillLabel,
  jobStateLabel,
  retryViaThinkText,
  sortJobsForPill,
  thinkJobResultChips,
  thinkJobRunnerNote,
  thinkJobTitle,
  type PcbxJobWake,
  type ThinkJob,
  type ThinkJobState,
} from "@/lib/think-jobs";
import { cn } from "@/lib/utils";
import {
  ArchiveIcon,
  BanIcon,
  CheckIcon,
  GitCompareIcon,
  ListChecksIcon,
  RotateCcwIcon,
  SearchIcon,
  SquareArrowOutUpRightIcon,
  WrenchIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

const ACTION_BTN =
  "h-[26px] gap-1 rounded-[2px] px-2.5 text-[11px] font-normal [&_svg]:size-3";

function matchPrefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(matchPrefersReducedMotion);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function useJobNow(now: number | undefined, ticking: boolean): number {
  const [tick, setTick] = useState(() => now ?? Date.now());
  useEffect(() => {
    if (now != null) {
      setTick(now);
      return;
    }
    if (!ticking) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [now, ticking]);
  return now ?? tick;
}

function StatusGlyph({ state }: { state: ThinkJobState }) {
  if (state === "queued") {
    return (
      <span
        aria-hidden
        className="size-2 shrink-0 rounded-full border"
        style={{ borderColor: JOB_INK_55, background: "transparent" }}
      />
    );
  }
  if (state === "running") {
    return <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: JOB_COPPER }} />;
  }
  if (state === "succeeded") {
    return <CheckIcon aria-hidden className="size-3.5 shrink-0" style={{ color: JOB_OK }} />;
  }
  if (state === "failed") {
    return <XIcon aria-hidden className="size-3.5 shrink-0" style={{ color: JOB_FALCON }} />;
  }
  return <BanIcon aria-hidden data-testid="job-card-ban" className="size-3.5 shrink-0" style={{ color: JOB_CANCELLED }} />;
}

function JobKindIcon({ kind }: { kind: string }) {
  const cls = "size-3.5 shrink-0 text-muted-foreground";
  switch (kind) {
    case "import_project":
      return <ArchiveIcon aria-hidden className={cls} />;
    case "run_checks":
      return <ListChecksIcon aria-hidden className={cls} />;
    case "circuit_diff":
      return <GitCompareIcon aria-hidden className={cls} />;
    case "agent_findings":
      return <SearchIcon aria-hidden className={cls} />;
    case "cancel_job":
      return <BanIcon aria-hidden className={cls} />;
    default:
      return <WrenchIcon aria-hidden className={cls} />;
  }
}

function FailedErrorBox({ error }: { error: string }) {
  const [open, setOpen] = useState(false);
  const shown = userFacingJobError(error) ?? error;
  const long = shown.split("\n").length > 3 || shown.length > 240;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <div
        data-testid="job-card-error"
        className="max-w-full rounded-[2px] border px-2 py-1.5"
        style={{
          color: "#F2B8BB",
          background: "rgba(227, 28, 37, 0.07)",
          borderColor: "rgba(227, 28, 37, 0.35)",
        }}
      >
        <pre
          className={cn(
            "max-w-full font-mono text-[11px] leading-4 whitespace-pre-wrap",
            !open && "line-clamp-3 overflow-hidden",
          )}
        >
          {shown}
        </pre>
      </div>
      {long && (
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="mt-1 font-mono text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          >
            {open ? "Collapse error" : "Expand error"}
          </button>
        </CollapsibleTrigger>
      )}
    </Collapsible>
  );
}

function ActionButton({
  children,
  icon,
  testId,
  onClick,
}: {
  children: ReactNode;
  icon: ReactNode;
  testId: string;
  onClick?: () => void;
}) {
  return (
    <Button type="button" variant="outline" className={ACTION_BTN} data-testid={testId} onClick={onClick}>
      {icon}
      {children}
    </Button>
  );
}

export function JobCard({
  job,
  now,
  onOpen,
  onCancel,
  onRetry,
  onOpenReview,
}: {
  job: ThinkJob;
  now?: number;
  onOpen?: (job: ThinkJob) => void;
  onCancel?: (job: ThinkJob) => void;
  onRetry?: (job: ThinkJob) => void;
  onOpenReview?: (job: ThinkJob) => void;
}) {
  const ticking = isActiveJobState(job.state);
  const clockNow = useJobNow(now, ticking);
  const clock = formatJobClock(jobElapsedMs(job, clockNow));
  const title = thinkJobTitle(job);
  const chips = job.state === "succeeded" ? thinkJobResultChips(job) : [];
  const dimmed = job.state === "cancelled";
  const canCancel = job.state === "queued" || job.state === "running";
  const showRetry = job.state === "failed";
  const showReview = Boolean(job.reviewId) && (job.kind === "circuit_diff" || job.kind === "agent_findings");
  const reduceMotion = usePrefersReducedMotion();
  const runnerNote = thinkJobRunnerNote(job);

  return (
    <Item
      id={jobCardDomId(job.id)}
      data-testid="job-card"
      data-job-id={job.id}
      data-job-state={job.state}
      data-job-kind={job.kind}
      variant="outline"
      className={cn(
        "w-full flex-col items-stretch gap-1.5 overflow-hidden rounded-[4px]",
        dimmed && "border-[rgba(110,110,115,0.35)]",
      )}
      style={{
        background: dimmed ? "#101010" : JOB_RAISED,
        padding: "10px 12px 10px 14px",
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: dimmed ? "rgba(110, 110, 115, 0.35)" : JOB_HAIRLINE,
        borderLeftWidth: 2,
        borderLeftColor: jobBarColor(job.state),
      }}
    >
      <ItemHeader className="items-center">
        <div className="flex min-w-0 items-center gap-1.5">
          <StatusGlyph state={job.state} />
          <JobKindIcon kind={job.kind} />
          <span className="font-mono text-[11px] text-muted-foreground">{job.kind}</span>
        </div>
        <Badge
          variant="outline"
          className="h-5 rounded-full px-2 font-mono text-[11px] font-normal tracking-[0.02em]"
          style={{
            color:
              job.state === "succeeded"
                ? JOB_OK
                : job.state === "failed"
                  ? JOB_FALCON
                  : job.state === "running"
                    ? JOB_COPPER
                    : job.state === "cancelled"
                      ? JOB_CANCELLED
                      : JOB_INK_55,
            borderColor: JOB_HAIRLINE,
          }}
        >
          {jobStateLabel(job.state)}
        </Badge>
      </ItemHeader>

      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="text-[13px] font-medium text-foreground">{title}</ItemTitle>
        <ItemDescription className="line-clamp-none text-[11px]">
          <span className="font-mono text-[11px]" style={{ color: JOB_META }}>
            {job.id}
            {job.state === "queued" ? " waiting " : job.state === "cancelled" ? " at " : job.state === "failed" ? " after " : " "}
            <span data-testid="job-card-elapsed">{clock}</span>
            {job.state === "succeeded" && formatJobEndTime(job.finishedAt)
              ? ` · ${formatJobEndTime(job.finishedAt)}`
              : job.state === "cancelled" && job.cancelledBy?.trim()
                ? ` · by ${job.cancelledBy.trim()}`
                : ""}
          </span>
        </ItemDescription>
      </ItemContent>

      {job.state === "running" && (
        <>
          <div className="job-indeterminate-bar w-full" data-testid="job-card-indeterminate" aria-hidden />
          {reduceMotion ? (
            <p className="font-mono text-[11px] text-muted-foreground">{runnerNote}</p>
          ) : (
            <Shimmer as="p" className="font-mono text-[11px] text-muted-foreground" duration={1.6}>
              {runnerNote}
            </Shimmer>
          )}
        </>
      )}

      {job.state === "succeeded" && chips.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="job-card-result">
          {chips.map((chip) => (
            <Badge
              key={chip}
              variant="outline"
              className="h-5 rounded-full px-2 font-mono text-[11px] font-normal text-foreground"
            >
              {chip}
            </Badge>
          ))}
        </div>
      )}

      {job.state === "failed" && job.error && <FailedErrorBox error={job.error} />}

      <ItemFooter className="mt-0.5 justify-start">
        <ItemActions className="flex-wrap">
          <ActionButton
            testId="job-card-open"
            icon={<SquareArrowOutUpRightIcon />}
            onClick={() => onOpen?.(job)}
          >
            Open job
          </ActionButton>
          {canCancel && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <ActionButton testId="job-card-cancel" icon={<BanIcon />} onClick={() => onCancel?.(job)}>
                    Cancel
                  </ActionButton>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{cancelViaThinkText(job)}</TooltipContent>
            </Tooltip>
          )}
          {showRetry && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <ActionButton
                    testId="job-card-retry"
                    icon={<RotateCcwIcon />}
                    onClick={() => onRetry?.(job)}
                  >
                    Retry via Think
                  </ActionButton>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{retryViaThinkText(job)}</TooltipContent>
            </Tooltip>
          )}
          {showReview && onOpenReview && (
            <ActionButton
              testId="job-card-open-review"
              icon={<SquareArrowOutUpRightIcon />}
              onClick={() => onOpenReview(job)}
            >
              Open change{job.reviewNumber != null ? ` #${job.reviewNumber}` : ""}
            </ActionButton>
          )}
        </ItemActions>
      </ItemFooter>
    </Item>
  );
}

export function JobWakeMarker({ wake }: { wake: PcbxJobWake }) {
  return (
    <div
      className="flex w-full min-w-0 justify-center py-1"
      data-testid="job-wake-marker"
      data-job-id={wake.id}
      data-job-state={wake.state}
    >
      <span
        data-testid="job-wake-chip"
        className="inline-flex h-5 items-center rounded-full border px-2 font-mono text-[10px] tracking-[0.04em] uppercase"
        style={{ color: JOB_META, borderColor: JOB_HAIRLINE }}
      >
        {jobStateLabel(wake.state)}
      </span>
    </div>
  );
}

export function ThinkJobsPill({
  jobs,
  onSelectJob,
}: {
  jobs: readonly ThinkJob[];
  onSelectJob?: (job: ThinkJob) => void;
}) {
  const ordered = useMemo(() => sortJobsForPill(jobs), [jobs]);
  const [open, setOpen] = useState(false);
  const activeJob = useMemo(() => ordered.find((job) => isActiveJobState(job.state)), [ordered]);
  const latest = ordered[0];
  const ticking = Boolean(activeJob);
  const clockNow = useJobNow(undefined, ticking);
  if (ordered.length === 0) return null;
  const clockJob = activeJob ?? null;
  const liveClock =
    clockJob != null ? formatJobClock(jobElapsedMs(clockJob, clockNow)) : null;
  const terminalHint =
    !liveClock && latest != null ? formatJobPillTerminalHint(latest, clockNow) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="think-jobs-pill"
          className="inline-flex h-5 max-w-full items-center gap-1 rounded-full border border-border px-2 font-mono text-[11px] text-muted-foreground hover:bg-white/[0.08] hover:text-foreground"
        >
          {jobPillLabel(ordered.length)}
          {liveClock != null ? (
            <span data-testid="job-pill-live-clock">{liveClock}</span>
          ) : terminalHint != null ? (
            <span data-testid="job-pill-terminal-hint">{terminalHint}</span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 bg-[#0C0C0C] p-2"
        data-testid="think-jobs-popover"
        aria-label="Think jobs"
      >
        <div className="flex max-h-80 flex-col gap-2 overflow-auto">
          {ordered.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              now={clockNow}
              onOpen={(next) => {
                onSelectJob?.(next);
                setOpen(false);
              }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function scrollToJobCard(jobId: string): void {
  if (typeof document === "undefined") return;
  const reduce = matchPrefersReducedMotion();
  document.getElementById(jobCardDomId(jobId))?.scrollIntoView({
    behavior: reduce ? "auto" : "smooth",
    block: "center",
  });
}
